/**
 * ReconciliationReportPage — `/reports/reconciliation`.
 *
 * Wired to GET /v1/reports/reconciliation?period= on service-reporting-api.
 * Note: service-finance settlement data is not yet in SourceReaders —
 * this report returns stub data until loadFinanceSettlements() is wired.
 */
import { type JSX, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Banknote } from 'lucide-react';

import { getReconciliationReport } from '../../lib/api/pre-built-reports';
import { ApiError } from '../../lib/api/client';
import { findPreBuiltReport } from '../../lib/api/reports';
import { DeferredNotice } from '../shared/StaffUI';
import { KPICard, formatNaira } from '../mail/shared/MailUI';
import { ReportShell } from './ReportShell';

function fmtSigned(minor: number): string {
  if (minor < 0) return `-${formatNaira(-minor)}`;
  return formatNaira(minor);
}

export function ReconciliationReportPage(): JSX.Element {
  const report = findPreBuiltReport('reconciliation');
  const [period, setPeriod] = useState<string>('THIS_MONTH');

  const query = useQuery({
    queryKey: ['pre-built', 'reconciliation', period],
    queryFn: () => getReconciliationReport(period),
    retry: false,
  });

  if (!report) return <p>Unknown report.</p>;

  const data = query.data;
  const denied =
    query.isError && query.error instanceof ApiError &&
    (query.error.status === 401 || query.error.status === 403);

  return (
    <ReportShell report={report}>
      <div className="bg-white border border-border rounded-xl p-5">
        <div className="flex items-end gap-3">
          <div>
            <label className="text-[10px] font-semibold text-muted uppercase tracking-widest mb-1 block">Period</label>
            <select value={period} onChange={(e) => setPeriod(e.target.value)} aria-label="Period"
              className="px-3 py-2 text-sm border border-border rounded-lg bg-white">
              <option value="THIS_MONTH">This month</option>
              <option value="LAST_MONTH">Last month</option>
              <option value="THIS_YEAR">This year</option>
            </select>
          </div>
        </div>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
        <KPICard title="Total ledger" value={data ? formatNaira(data.totalLedgerMinor) : '—'} hint="Finance ledger" icon={Banknote} />
        <KPICard title="Total settled" value={data ? formatNaira(data.totalSettledMinor) : '—'} hint="Settlements" />
        <KPICard title="Net variance" value={data ? fmtSigned(data.netVarianceMinor) : '—'} hint="ledger − settled" tone={data && data.netVarianceMinor !== 0 ? 'warning' : undefined} />
        <KPICard title="Channels" value={data ? data.channels.length.toLocaleString('en-US') : '—'} hint="payment channels" />
      </div>

      {query.isPending && <p className="text-sm text-muted">Loading report…</p>}
      {denied && (
        <DeferredNotice title="Permission required">
          <code className="font-mono">Reports/readReconciliation</code> required.
        </DeferredNotice>
      )}
      {query.isError && !denied && (
        <DeferredNotice title="Report unavailable">{query.error.message}</DeferredNotice>
      )}

      {data && (
        <div className="bg-white border border-border rounded-xl p-5">
          <div className="flex items-center justify-between mb-3">
            <h3 className="font-semibold text-gray-900">Channel breakdown</h3>
            <span className="text-[10px] font-medium text-amber-700 bg-amber-50 px-2 py-0.5 rounded-full">
              Preview data — full data coming soon
            </span>
          </div>
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-gray-50 text-[10px] uppercase tracking-wider text-muted">
                <tr>
                  <th className="text-left px-4 py-2">Channel</th>
                  <th className="text-right px-4 py-2">Ledger</th>
                  <th className="text-right px-4 py-2">Settled</th>
                  <th className="text-right px-4 py-2">Variance</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-border">
                {data.channels.map((s) => (
                  <tr key={s.channel}>
                    <td className="px-4 py-2 text-xs">{s.channel}</td>
                    <td className="px-4 py-2 text-right text-xs">{formatNaira(s.ledgerMinor)}</td>
                    <td className="px-4 py-2 text-right text-xs">{formatNaira(s.settledMinor)}</td>
                    <td className={`px-4 py-2 text-right text-xs ${s.varianceMinor === 0 ? 'text-muted' : 'text-red-600'}`}>
                      {fmtSigned(s.varianceMinor)}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </ReportShell>
  );
}
